import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Row,
  Col,
  Space,
  Button,
  Switch,
  Spin
} from '@lockerpm/design';

import commonComponents from "../../../components/common";
import privateEmailComponents from "./components";

import relayServices from "../../../services/relay";
import global from "../../../config/global";
import common from "../../../utils/common";

const PrivateEmail = () => {
  const { PageHeader } = commonComponents;
  const {
    Information,
    AliasCard,
    AliasForm,
    DeleteModal,
    SubdomainForm
  } = privateEmailComponents;

  const { t } = useTranslation();
  const plan = useSelector((state) => state.payment.plan);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const planLimit = useSelector((state) => state.payment.planLimit);

  const [loading, setLoading] = useState(false);
  const [useSubdomain, setUseSubdomain] = useState(false);
  const [aliases, setAliases] = useState([]);
  const [subdomains, setSubdomains] = useState([]);
  const [aliasVisible, setAliasVisible] = useState(false);
  const [subdomainVisible, setSubdomainVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [selectedAlias, setSelectedAlias] = useState(null);
  const [creating, setCreating] = useState(false);
  const [using, setUsing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [])

  const isPremium = useMemo(() => {
    return common.isPremiumFeaturesAvailable();
  }, [userInfo, plan])

  const isLimited = useMemo(() => {
    return aliases.length >= planLimit?.plan_limit?.relay_addresses
  }, [aliases, planLimit])

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      getUseSubDomain(),
      getSubdomains(),
      getAddresses(),
    ])
    setLoading(false);
  }

  const getUseSubDomain = async () => {
    const res = await relayServices.use_subdomain();
    setUseSubdomain(res.use_relay_subdomain ||  false)
  }
  const getSubdomains = async () => {
    const res = await relayServices.get_subdomains({ paging: 0 });
    setSubdomains(res)
  }
  const getAddresses = async () => {
    const res = await relayServices.get_addresses({ paging: 0 });
    setAliases(res)
  }

  const addAddress = async () => {
    setCreating(true);
    await relayServices.create_address().then(async () => {
      await getAddresses();
      setCreating(false);
    }).catch((error) => {
      const { code } = common.getErrorMessage(error)
      if (code === "429") {
        global.pushError({ message: t('notification.error.alias.limited') })
      } else {
        global.pushError(error);
      }
      setCreating(false);
    });
  }

  const toggleSubdomain = async (isEnabled) => {
    setUsing(true);
    await relayServices.toggle_use_subdomain({
      use_relay_subdomain: isEnabled
    }).then(async () => {
      setUseSubdomain(isEnabled)
      if (subdomains.length === 0) {
        setSubdomainVisible(true);
      }
    }).catch((error) => {
      global.pushError(error)
    });
    setUsing(false);

  }

  const closeAliasForm = async (isReload) => {
    setAliasVisible(false);
    if (isReload) {
      await getAddresses();
    }
  }

  const closeDeleteModal = async (isReload) => {
    setDeleteVisible(false);
    if (isReload) {
      await getAddresses();
    }
  }

  const closeSubdomainForm = async (isReload) => {
    setSubdomainVisible(false);
    if (isReload) {
      await fetchData();
    }
  }

  const handleAddAddress = () => {
    if (!isLimited) {
      addAddress();
    } else {
      common.togglePlanNoticeModal(() => addAddress())
    }
  }

  return (
    <div className="private-email layout-content">
      <PageHeader
        title={t('private_email.title')}
        actions={[]}
      />
      <Information
        isPremium={isPremium}
      />
      <div className="my-6">
        <Spin spinning={loading}>
          <Row gutter={[24, 24]}>
            <Col lg={12} span={24}>
              <p className="font-semibold text-xl">{t('private_email.alias')} ({aliases.length})</p>
            </Col>
            <Col lg={12} span={24} align="right">
              <Space>
                {
                  isPremium && <>
                    <div className="flex items-center">
                      <Switch
                        checked={useSubdomain}
                        loading={using}
                        onChange={(v) => toggleSubdomain(v)}
                      />
                      <p className="ml-2">{t('private_email.use_subdomain')}</p>
                    </div>
                    <Button
                      type="primary"
                      ghost
                      onClick={() => setSubdomainVisible(true)}
                    >
                      {t('private_email.manage_subdomain.title')}
                    </Button>
                  </>
                }
                <Button
                  type="primary"
                  loading={creating}
                  onClick={() => handleAddAddress()}
                >
                  {t('private_email.new_alias')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Spin>
      </div>
      <Row gutter={[16, 16]}>
        {
          aliases.map((alias, index) => <Col key={index} lg={8} md={12} sm={12} span={24}>
            <AliasCard
              className="h-full"
              alias={alias}
              onEdit={() => {
                setSelectedAlias(alias);
                setAliasVisible(true)
              }}
              onDelete={() => {
                setSelectedAlias(alias);
                setDeleteVisible(true)
              }}
            />
          </Col>)
        }
      </Row>
      <AliasForm
        item={selectedAlias}
        visible={aliasVisible}
        isEdit={aliases.findIndex((a) => a.id === selectedAlias?.id) === 0}
        onClose={closeAliasForm}
      />
      {
        isPremium && <SubdomainForm
          visible={subdomainVisible}
          subdomains={subdomains}
          onClose={closeSubdomainForm}
        />
      }
      <DeleteModal
        item={selectedAlias}
        visible={deleteVisible}
        onClose={closeDeleteModal}
      />
    </div>
  );
}

export default PrivateEmail;